import React, { useState, useEffect } from 'react';
import { 
    LayoutDashboard, BookOpen, Video, Download, 
    Award, Bell, Settings, LogOut, Menu, X,
    PlayCircle, Calendar, Clock, CheckCircle2,
    FileText, ChevronRight, Star, ShieldCheck, Laptop,
    Users, ExternalLink, CreditCard, List
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyCourses, getCourseContent } from '../../services/learningService';
import CourseGrid from './CourseGrid';
import UpcomingEventsPlaceholder from './UpcomingEventsPlaceholder';
import RecentRecordingsPlaceholder from './RecentRecordingsPlaceholder';
import SoftwarePlaceholder from './SoftwarePlaceholder';
import CertificatesPlaceholder from './CertificatesPlaceholder';

const Campus = () => {
    const { user: authUser, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('dashboard');
    const [myCourses, setMyCourses] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [myCertificates, setMyCertificates] = useState([]);
    
    // Modules Modal State
    const [showModulesModal, setShowModulesModal] = useState(false);
    const [selectedCourseForModules, setSelectedCourseForModules] = useState(null);
    const [courseModulesDetails, setCourseModulesDetails] = useState(null);
    const [loadingModules, setLoadingModules] = useState(false);

    useEffect(() => {
        if (activeSection === 'courses' || activeSection === 'dashboard') {
            loadCourses();
        }
    }, [activeSection]);

    const loadCourses = async () => {
        setLoadingCourses(true);
        try {
            // Use SQL ID (authUser.id) if available, otherwise fallback to 1 for testing
            const clientId = authUser?.id || 1; 
            const data = await getMyCourses(clientId);
            setMyCourses(data);
        } catch (error) {
            console.error('Error loading courses:', error);
        } finally {
            setLoadingCourses(false);
        }
    };

    const handleShowModules = async (course) => {
        setSelectedCourseForModules(course);
        setShowModulesModal(true);
        setLoadingModules(true);
        try {
            const clientId = authUser?.id || 1;
            const details = await getCourseContent(course.id, clientId);
            setCourseModulesDetails(details);
        } catch (error) {
            console.error('Error loading modules:', error);
        } finally {
            setLoadingModules(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Mock Data (merged with auth user)
    const user = {
        name: authUser?.displayName || "Juan Pérez",
        role: authUser?.role || "Compliance Officer",
        avatar: authUser?.displayName ? authUser.displayName.split(' ').map(n => n[0]).join('').substring(0, 2) : "JP",
        company: authUser?.company || "Banco Finanzas S.A."
    };

    const upcomingLive = [];

    const softwareCapsules = [
        // {
        //     id: 1,
        //     title: "Script de Validación de CUITs",
        //     type: "Python Script",
        //     version: "v2.1",
        //     description: "Automatiza la verificación de CUITs en bases de datos masivas contra padrón AFIP."
        // },
        // {
        //     id: 2,
        //     title: "Reporte de Operaciones Sospechosas (Template)",
        //     type: "Reporte .RPT",
        //     version: "v1.0",
        //     description: "Plantilla estándar para exportación de ROS compatible con el nuevo sistema de la UIF."
        // },
        // {
        //     id: 3,
        //     title: "Add-on de Monitoreo Transaccional",
        //     type: "Plugin",
        //     version: "v3.0.5",
        //     description: "Extensión para visualizar grafos de relaciones entre clientes."
        // }
    ];

    const [courseFilter, setCourseFilter] = useState('all'); // 'all', 'in-progress', 'completed'

    const renderContent = () => {
        switch(activeSection) {
            case 'dashboard':
                return (
                    <div className="space-y-8 animate-fade-in-up">
                        {/* Welcome Banner */}
                        <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden shadow-xl">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
                            <div className="relative z-10">
                                <h2 className="text-3xl font-bold mb-2">Hola, {user.name}</h2>
                                <p className="text-slate-300">Tienes {myCourses.filter(c => !c.isCompleted).length} cursos en progreso y 0 clases en vivo esta semana.</p>
                            </div>
                        </div>

                        {/* Progress Section */}
                        <CourseGrid courses={myCourses} />

                        {/* Dashboard Grid */}
                        <div className="grid lg:grid-cols-3 gap-8">
                            {/* Live Classes Widget */}
                            <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-bold text-slate-800">Próximas Clases en Vivo</h3>
                                    <button 
                                        onClick={() => setActiveSection('live')}
                                        className="text-blue-600 text-sm font-bold hover:underline"
                                    >
                                        Ver Calendario
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {upcomingLive.length > 0 ? (
                                        upcomingLive.map(evt => (
                                            <div key={evt.id} className="flex items-center gap-4 p-4 rounded-lg bg-slate-50 border border-slate-100 hover:border-blue-200 transition-colors">
                                                <div className="flex-shrink-0 w-14 h-14 bg-white rounded-lg border border-slate-200 flex flex-col items-center justify-center text-slate-700 font-bold shadow-sm">
                                                    <span className="text-xs text-red-500 uppercase">Dic</span>
                                                    <span className="text-xl">{evt.date.split(' ')[0]}</span>
                                                </div>
                                                <div className="flex-grow">
                                                    <h4 className="font-bold text-slate-900">{evt.title}</h4>
                                                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                                        <span className="flex items-center gap-1"><Clock size={14}/> {evt.time}</span>
                                                        <span className="flex items-center gap-1"><Users size={14}/> {evt.instructor}</span>
                                                    </div>
                                                </div>
                                                <button className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-blue-600/20">
                                                    Unirse
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <UpcomingEventsPlaceholder />
                                    )}
                                </div>
                            </div>

                            {/* Quick Stats / Certificates */}
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col">
                                <h3 className="text-lg font-bold text-slate-800 mb-6">Mis Logros</h3>
                                <div className="space-y-4 mb-6">
                                    {myCertificates.length > 0 ? (
                                        myCertificates.map(cert => (
                                            <div key={cert.id} className="flex items-center gap-3 p-3 rounded-lg bg-green-50 text-green-800 border border-green-100">
                                                <Award size={20} />
                                                <div>
                                                    <p className="text-sm font-bold">{cert.title}</p>
                                                    <p className="text-xs opacity-80">Certificado Emitido</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                                            <p className="text-sm text-slate-500">Cuando obtengas certificaciones, aparecerán aquí.</p>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-auto pt-6 border-t border-slate-100">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-bold text-slate-700">Cápsulas Recientes</h4>
                                        <button onClick={() => setActiveSection('software')} className="text-xs text-blue-600 font-bold hover:underline">Ver todas</button>
                                    </div>
                                    <ul className="space-y-2">
                                        {softwareCapsules.length > 0 ? (
                                            softwareCapsules.slice(0, 2).map(cap => (
                                                <li key={cap.id} className="flex items-center justify-between text-sm text-slate-600 hover:text-blue-600 cursor-pointer group p-2 hover:bg-slate-50 rounded transition-colors">
                                                    <span className="truncate max-w-[180px] font-medium">{cap.title}</span>
                                                    <Download size={14} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                                                </li>
                                            ))
                                        ) : (
                                            <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                                                <p className="text-xs text-slate-500">Cuando haya cápsulas de software disponibles, aparecerán aquí.</p>
                                            </div>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            
            case 'courses':
                return (
                    <div className="space-y-8 animate-fade-in-up">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Mis Cursos</h2>
                                <p className="text-slate-500">Gestiona tu aprendizaje y continua donde lo dejaste.</p>
                            </div>
                        </div>

                        <CourseGrid courses={myCourses} title={null} />
                    </div>
                );
            
            case 'software':
                return (
                    <div className="space-y-8 animate-fade-in-up">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Cápsulas de Software</h2>
                                <p className="text-slate-500">Herramientas, scripts y reportes para potenciar tu sistema.</p>
                            </div>
                            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-bold border border-indigo-100 flex items-center gap-2">
                                <Laptop size={16} />
                                Licencia Enterprise Activa
                            </div>
                        </div>

                        <SoftwarePlaceholder />
                        {/* <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {softwareCapsules.map(cap => (
                                <div key={cap.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="p-3 bg-slate-100 rounded-lg text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                            <Download size={24} />
                                        </div>
                                        <span className="text-xs font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                            {cap.type}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">{cap.title}</h3>
                                    <p className="text-sm text-slate-500 mb-4 min-h-[3rem]">{cap.description}</p>
                                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                        <span className="text-xs font-mono text-slate-400">{cap.version}</span>
                                        <button className="flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors">
                                            Descargar
                                            <Download size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div> */}
                    </div>
                );

            case 'live':
                return (
                    <div className="space-y-8 animate-fade-in-up">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Clases en Vivo</h2>
                                <p className="text-slate-500">Inscribite a las próximas sesiones o mira las grabaciones disponibles.</p>
                            </div>
                            <button className="px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2">
                                <Calendar size={16} />
                                Ver Calendario Completo
                            </button>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-6">
                                <h3 className="text-lg font-bold text-slate-800">Próximos Eventos</h3>
                                {upcomingLive.length > 0 ? (
                                    upcomingLive.map(evt => (
                                        <div key={evt.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                                            <div className="flex-shrink-0 w-20 h-20 bg-slate-50 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-slate-700 font-bold shadow-inner">
                                                <span className="text-xs text-red-500 uppercase tracking-wider">Dic</span>
                                                <span className="text-3xl">{evt.date.split(' ')[0]}</span>
                                            </div>
                                            <div className="flex-grow">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${evt.status === 'Confirmado' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                                                        {evt.status}
                                                    </span>
                                                    <span className="text-xs text-slate-400 font-medium">Webinar Exclusivo</span>
                                                </div>
                                                <h4 className="text-xl font-bold text-slate-900 mb-2">{evt.title}</h4>
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                                    <span className="flex items-center gap-1.5"><Clock size={16} className="text-blue-500"/> {evt.time}</span>
                                                    <span className="flex items-center gap-1.5"><Users size={16} className="text-purple-500"/> {evt.instructor}</span>
                                                    <span className="flex items-center gap-1.5"><Video size={16} className="text-slate-400"/> Zoom Live</span>
                                                </div>
                                            </div>
                                            <button className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 whitespace-nowrap">
                                                Reservar Lugar
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <UpcomingEventsPlaceholder />
                                )}
                            </div>

                            <div className="space-y-6">
                                <h3 className="text-lg font-bold text-slate-800">Grabaciones Recientes</h3>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-4">
                                    <RecentRecordingsPlaceholder />
                                    {/* {[1, 2, 3].map((i) => (
                                        <div key={i} className="group cursor-pointer">
                                            <div className="relative rounded-lg overflow-hidden mb-3 aspect-video bg-slate-900">
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                                                    <PlayCircle size={32} className="text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                                                </div>
                                            </div>
                                            <h5 className="font-bold text-slate-900 text-sm leading-tight group-hover:text-blue-600 transition-colors">
                                                Análisis de Casos Reales: Estafas Piramidales
                                            </h5>
                                            <p className="text-xs text-slate-400 mt-1">Hace 2 semanas • 45 min</p>
                                        </div>
                                    ))} */}
                                    <button className="w-full py-2 text-sm font-bold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                        Ver Biblioteca Completa
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'certificates':
                return (
                    <div className="space-y-8 animate-fade-in-up">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Mis Certificaciones</h2>
                                <p className="text-slate-500">Descarga y compartí tus logros académicos.</p>
                            </div>
                            <button className="px-4 py-2 bg-slate-900 text-white text-sm font-bold rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2">
                                <Award size={16} />
                                Verificar Credencial
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {myCertificates.length > 0 ? (
                                myCertificates.map(cert => (
                                    <div key={cert.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                        <div className={`h-48 ${cert.image} p-8 relative flex items-center justify-center text-center`}>
                                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                                            <div className="relative z-10 text-white">
                                                <Award size={48} className="mx-auto mb-4 opacity-90" />
                                                <h3 className="text-2xl font-serif font-bold tracking-wide mb-1">{cert.title}</h3>
                                                <p className="text-white/80 text-sm">Otorgado a {user.name}</p>
                                            </div>
                                        </div>
                                        <div className="p-6 flex flex-col gap-4">
                                            <div className="flex justify-between items-center text-sm text-slate-500 border-b border-slate-100 pb-4">
                                                <span>Fecha de Emisión: <strong>{cert.date}</strong></span>
                                                <span>ID: <strong>{cert.code}</strong></span>
                                            </div>
                                            <div className="flex gap-3">
                                                <button className="flex-1 py-2.5 bg-slate-50 text-slate-700 font-bold text-sm rounded-lg hover:bg-slate-100 border border-slate-200 transition-colors flex items-center justify-center gap-2">
                                                    <Download size={16} />
                                                    PDF
                                                </button>
                                                <button className="flex-1 py-2.5 bg-blue-600 text-white font-bold text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2">
                                                    <ExternalLink size={16} />
                                                    LinkedIn
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <CertificatesPlaceholder />
                            )}
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="flex flex-col items-center justify-center h-96 text-center animate-fade-in-up">
                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center text-slate-300 mb-6">
                            <Settings size={40} />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Sección en Construcción</h2>
                        <p className="text-slate-500 max-w-md">
                            Estamos preparando contenido exclusivo para esta sección. Vuelve pronto para ver las novedades.
                        </p>
                        <button 
                            onClick={() => setActiveSection('dashboard')}
                            className="mt-6 px-6 py-2 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors"
                        >
                            Volver al Dashboard
                        </button>
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex font-sans">
            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 flex flex-col`}>
                <div className="p-6 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3 font-bold text-xl tracking-tight">
                        <img src="/xp_dark.png" alt="X Project" className="h-6" />
                        <span>Campus</span>
                    </div>
                    <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
                        <X size={24} />
                    </button>
                </div>

                <nav className="px-4 space-y-1 mt-4 flex-1 overflow-y-auto">
                    {[
                        { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
                        { id: 'courses', label: 'Mis Cursos', icon: <BookOpen size={20} /> },
                        { id: 'live', label: 'Clases en Vivo', icon: <Video size={20} /> },
                        { id: 'software', label: 'Cápsulas Software', icon: <Laptop size={20} /> },
                        { id: 'certificates', label: 'Certificados', icon: <Award size={20} /> },
                    ].map(item => (
                        <button
                            key={item.id}
                            onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                                activeSection === item.id 
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 font-bold' 
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white font-medium'
                            }`}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="p-4 shrink-0 border-t border-slate-800">
                    <div className="bg-slate-800/50 rounded-xl p-4 flex items-center gap-3 mb-4 border border-slate-700/50">
                        <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center font-bold text-sm text-white shadow-md">
                            {user.avatar}
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-sm font-bold truncate text-white">{user.name}</p>
                            <p className="text-xs text-slate-400 truncate">{user.company}</p>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="w-full flex items-center gap-2 text-slate-400 hover:text-white text-sm px-2 transition-colors font-medium">
                        <LogOut size={16} />
                        Cerrar Sesión
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Header */}
                <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-8 shrink-0 z-10">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-500 hover:text-slate-700">
                        <Menu size={24} />
                    </button>
                    
                    <div className="flex items-center gap-4 ml-auto">
                        <button className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors">
                            <Bell size={20} />
                            {/* <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span> */}
                        </button>
                        <div className="h-8 w-px bg-slate-200 mx-2"></div>
                        <div className="text-right hidden sm:block">
                            <p className="text-xs font-bold text-slate-900">{user.company}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold text-blue-600">Plan Enterprise</p>
                        </div>
                    </div>
                </header>

                {/* Scrollable Content */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-50">
                    <div className="max-w-6xl mx-auto">
                        {renderContent()}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Campus;